import React, { useState, useEffect } from 'react';
import { Eye, EyeOff, HelpCircle, ChevronDown, RefreshCw, Tag, Info } from 'lucide-react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';



const QRContentForm = ({ 
  qrType, 
  content, 
  name, 
  onContentChange, 
  onNameChange,
  campaigns,
  selectedCampaign,
  onCampaignSelect,
  loadingCampaigns,
  onRefreshCampaigns,
  onCustomShortCodeChange,
  customShortCode,
  useCustomShortCode
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const [showCampaignDropdown, setShowCampaignDropdown] = useState(false);
  const [useCampaign, setUseCampaign] = useState(!!selectedCampaign);
  
  // NEW: Use props instead of local state for custom short code
  const [localCustomShortCode, setLocalCustomShortCode] = useState(customShortCode || '');
  const [localUseCustomShortCode, setLocalUseCustomShortCode] = useState(useCustomShortCode || false);
  const [shortCodeValidation, setShortCodeValidation] = useState(null);
  const [isValidatingShortCode, setIsValidatingShortCode] = useState(false);
  const [shortCodeSuggestions, setShortCodeSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isGeneratingSuggestions, setIsGeneratingSuggestions] = useState(false);

  // NEW: Sync with parent props
  useEffect(() => {
    if (customShortCode !== undefined) {
      setLocalCustomShortCode(customShortCode || '');
    }
  }, [customShortCode]);

  useEffect(() => {
    if (useCustomShortCode !== undefined) {
      setLocalUseCustomShortCode(useCustomShortCode || false);
    }
  }, [useCustomShortCode]);

  // NEW: Validate custom short code in real-time
  const validateShortCode = async (shortCode) => {
    if (!shortCode || shortCode?.length < 2) {
      setShortCodeValidation(null);
      setShowSuggestions(false);
      return;
    }

    setIsValidatingShortCode(true);
    try {
      const { TrackingUrlService } = await import('../../../services/trackingUrlService');
      const validation = await TrackingUrlService?.validateCustomShortCode(shortCode);
      
      setShortCodeValidation({
        isValid: validation?.isAvailable,
        error: validation?.error,
        suggestions: validation?.suggestions || []
      });

      if (!validation?.isAvailable && validation?.suggestions?.length > 0) {
        setShortCodeSuggestions(validation?.suggestions);
        setShowSuggestions(true);
      } else {
        setShowSuggestions(false);
      }
    } catch (error) {
      setShortCodeValidation({
        isValid: false,
        error: 'Failed to validate short code',
        suggestions: []
      });
    } finally {
      setIsValidatingShortCode(false);
    }
  };

  // NEW: Debounced validation
  useEffect(() => {
    if (!localUseCustomShortCode) return;
    
    const timer = setTimeout(() => {
      validateShortCode(localCustomShortCode);
    }, 500);

    return () => clearTimeout(timer);
  }, [localCustomShortCode, localUseCustomShortCode]);

  // NEW: Enhanced generate suggestions from name with better UX
  const generateSuggestionsFromName = async () => {
    if (!name?.trim()) {
      setShortCodeSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    setIsGeneratingSuggestions(true);
    try {
      const { TrackingUrlService } = await import('../../../services/trackingUrlService');
      const result = await TrackingUrlService?.getShortCodeSuggestions(name);
      
      if (result?.success && result?.suggestions?.length > 0) {
        setShortCodeSuggestions(result?.suggestions);
        setShowSuggestions(true);
        // Auto-enable custom short code section when suggestions are generated
        if (!localUseCustomShortCode) {
          setLocalUseCustomShortCode(true);
          onCustomShortCodeChange?.(localCustomShortCode, true);
        }
      } else {
        // Show message when no suggestions available
        setShortCodeSuggestions([]);
        setShowSuggestions(false);
      }
    } catch (error) {
      console.log('Failed to get suggestions:', error);
      setShortCodeSuggestions([]);
      setShowSuggestions(false);
    } finally {
      setIsGeneratingSuggestions(false);
    }
  };

  // NEW: Handle custom short code toggle with automatic suggestion generation
  const handleCustomShortCodeToggle = (enabled) => {
    setLocalUseCustomShortCode(enabled);
    if (!enabled) {
      setLocalCustomShortCode('');
      setShortCodeValidation(null);
      setShowSuggestions(false);
      // Notify parent that custom short code is disabled
      onCustomShortCodeChange?.(null, false);
    } else {
      // Notify parent that custom short code is enabled
      onCustomShortCodeChange?.(localCustomShortCode, true);
      
      // REMOVED: Automatic suggestion generation when checkbox is enabled
      // User wants suggestions ONLY when manually requested via buttons
    }
  };

  // NEW: Handle custom short code changes with proper callback
  const handleCustomShortCodeChange = (value) => {
    setLocalCustomShortCode(value);
    // Always notify parent of the change
    onCustomShortCodeChange?.(value, localUseCustomShortCode);
  };

  // NEW: Apply suggestion with proper callback
  const applySuggestion = (suggestion) => {
    setLocalCustomShortCode(suggestion);
    setShowSuggestions(false);
    // Notify parent of the suggestion
    onCustomShortCodeChange?.(suggestion, localUseCustomShortCode);
  };

  // Pass custom short code to parent component
  const handleQRDataChange = (field, value) => {
    if (field === 'customShortCode') {
      // Notify parent about custom short code
      if (onContentChange && typeof onContentChange === 'function') {
        // Create a custom property to pass short code info to parent
        onContentChange.__customShortCode = localUseCustomShortCode ? value : null;
      }
    }
  };

  const handleCampaignToggle = (checked) => {
    setUseCampaign(checked);
    if (!checked) {
      onCampaignSelect?.(null);
      setShowCampaignDropdown(false);
    } else {
      setShowCampaignDropdown(true);
    }
  };

  const handleCampaignSelect = (campaign) => {
    onCampaignSelect?.(campaign);
    setShowCampaignDropdown(false);
    setUseCampaign(true);
  };

  // URL validation and auto-formatting function
  const formatAndValidateUrl = (url) => {
    if (!url?.trim()) return url;
    
    let formattedUrl = url?.trim();
    
    // Auto-add protocol if missing
    if (!/^https?:\/\//i?.test(formattedUrl)) {
      // Check if it looks like a domain (contains dot and no spaces)
      if (formattedUrl?.includes('.') && !formattedUrl?.includes(' ')) {
        formattedUrl = 'https://' + formattedUrl;
      }
    }
    
    return formattedUrl;
  };

  const getFormFields = () => {
    switch (qrType) {
      case 'url':
        // Add missing parseUrlData function and formData/handleInputChange
        const urlData = content ? { websiteUrl: content } : {};
        const handleInputChange = (field, value) => {
          if (field === 'websiteUrl') {
            let formattedUrl = formatAndValidateUrl(value);
            onContentChange?.(formattedUrl);
          }
        };

        return (
          <div className="space-y-2">
            <label htmlFor="websiteUrl" className="text-sm font-medium text-gray-700 flex items-center">
              Website URL *
              <div className="ml-2 relative group">
                <Info size={16} className="text-gray-400 cursor-help" />
                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 hidden group-hover:block z-10">
                  <div className="bg-gray-800 text-white text-xs rounded py-2 px-3 whitespace-nowrap">
                    Enter the final destination URL where users will be redirected after scanning
                  </div>
                </div>
              </div>
            </label>
            <Input
              id="websiteUrl"
              type="url"
              placeholder="https://example.com - Enter your content/destination URL"
              value={urlData?.websiteUrl || ''}
              onChange={(e) => handleInputChange('websiteUrl', e?.target?.value)}
              className="w-full"
              required
            />
            <p className="text-xs text-gray-500">
              This is the original content URL where users will land after scanning the QR code. 
              A tracking URL will be automatically generated for analytics.
            </p>
          </div>
        );

      case 'vcard':
        const vcardData = parseVCardData(content);
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  First Name *
                </label>
                <input
                  type="text"
                  value={vcardData?.firstName || ''}
                  onChange={(e) => updateVCardField('firstName', e?.target?.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Last Name *
                </label>
                <input
                  type="text"
                  value={vcardData?.lastName || ''}
                  onChange={(e) => updateVCardField('lastName', e?.target?.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Phone Number
              </label>
              <input
                type="tel"
                value={vcardData?.phone || ''}
                onChange={(e) => updateVCardField('phone', e?.target?.value)}
                placeholder="+1 (555) 123-4567"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email Address
              </label>
              <input
                type="email"
                value={vcardData?.email || ''}
                onChange={(e) => updateVCardField('email', e?.target?.value)}
                placeholder="john@example.com"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Company/Organization
              </label>
              <input
                type="text"
                value={vcardData?.company || ''}
                onChange={(e) => updateVCardField('company', e?.target?.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        );

      case 'whatsapp':
        const whatsappData = parseWhatsAppData(content);
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Phone Number *
              </label>
              <input
                type="tel"
                value={whatsappData?.phone || ''}
                onChange={(e) => updateWhatsAppField('phone', e?.target?.value)}
                placeholder="+1234567890"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <p className="text-xs text-gray-500 mt-1">
                Include country code without '+' or spaces
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Message (Optional)
              </label>
              <textarea
                value={whatsappData?.message || ''}
                onChange={(e) => updateWhatsAppField('message', e?.target?.value)}
                placeholder="Hello! I found you through this QR code..."
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        );

      case 'email':
        const emailData = parseEmailData(content);
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email Address *
              </label>
              <input
                type="email"
                value={emailData?.email || ''}
                onChange={(e) => updateEmailField('email', e?.target?.value)}
                placeholder="contact@example.com"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Subject
              </label>
              <input
                type="text"
                value={emailData?.subject || ''}
                onChange={(e) => updateEmailField('subject', e?.target?.value)}
                placeholder="Inquiry from QR Code"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Message Body
              </label>
              <textarea
                value={emailData?.body || ''}
                onChange={(e) => updateEmailField('body', e?.target?.value)}
                placeholder="Hello, I'm contacting you through your QR code..."
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        );

      case 'phone':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Phone Number *
              </label>
              <input
                type="tel"
                value={content}
                onChange={(e) => onContentChange?.(e?.target?.value)}
                placeholder="+1 (555) 123-4567"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <p className="text-xs text-gray-500 mt-1">
                Include country code for international numbers
              </p>
            </div>
          </div>
        );

      case 'wifi':
        const wifiData = parseWiFiData(content);
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Network Name (SSID) *
              </label>
              <input
                type="text"
                value={wifiData?.ssid || ''}
                onChange={(e) => updateWiFiField('ssid', e?.target?.value)}
                placeholder="MyWiFiNetwork"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={wifiData?.password || ''}
                  onChange={(e) => updateWiFiField('password', e?.target?.value)}
                  placeholder="Network password"
                  className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Security Type
              </label>
              <select
                value={wifiData?.security || 'WPA'}
                onChange={(e) => updateWiFiField('security', e?.target?.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="WPA">WPA/WPA2</option>
                <option value="WEP">WEP</option>
                <option value="nopass">No Password</option>
              </select>
            </div>
          </div>
        );

      case 'sms':
        const smsData = parseSMSData(content);
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Phone Number *
              </label>
              <input
                type="tel"
                value={smsData?.phone || ''}
                onChange={(e) => updateSMSField('phone', e?.target?.value)}
                placeholder="+1 (555) 123-4567"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Message
              </label>
              <textarea
                value={smsData?.message || ''}
                onChange={(e) => updateSMSField('message', e?.target?.value)}
                placeholder="Your SMS message..."
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        );

      case 'text':
      default:
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Text Content *
              </label>
              <textarea
                value={content}
                onChange={(e) => onContentChange?.(e?.target?.value)}
                placeholder="Enter your text message..."
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <p className="text-xs text-gray-500 mt-1">
                Plain text that will be displayed when QR code is scanned
              </p>
            </div>
          </div>
        );
    }
  };

  // Helper functions for parsing and updating complex data types
  const parseVCardData = (vcardString) => {
    if (!vcardString) return {};
    try {
      const lines = vcardString?.split('\n');
      const data = {};
      lines?.forEach(line => {
        if (line?.includes('FN:')) data.firstName = line?.split('FN:')?.[1] || '';
        if (line?.includes('LN:')) data.lastName = line?.split('LN:')?.[1] || '';
        if (line?.includes('TEL:')) data.phone = line?.split('TEL:')?.[1] || '';
        if (line?.includes('EMAIL:')) data.email = line?.split('EMAIL:')?.[1] || '';
        if (line?.includes('ORG:')) data.company = line?.split('ORG:')?.[1] || '';
      });
      return data;
    } catch {
      return {};
    }
  };

  const updateVCardField = (field, value) => {
    const currentData = parseVCardData(content);
    const newData = { ...currentData, [field]: value };
    const vcard = `BEGIN:VCARD\nVERSION:3.0\nFN:${newData?.firstName || ''}\nLN:${newData?.lastName || ''}\nTEL:${newData?.phone || ''}\nEMAIL:${newData?.email || ''}\nORG:${newData?.company || ''}\nEND:VCARD`;
    onContentChange?.(vcard);
  };

  const parseWhatsAppData = (url) => {
    if (!url?.includes('wa.me')) return {};
    try {
      const urlObj = new URL(url);
      const phone = urlObj?.pathname?.slice(1);
      const message = urlObj?.searchParams?.get('text') || '';
      return { phone, message };
    } catch {
      return {};
    }
  };

  const updateWhatsAppField = (field, value) => {
    const currentData = parseWhatsAppData(content);
    const newData = { ...currentData, [field]: value };
    let url = `https://wa.me/${newData?.phone || ''}`;
    if (newData?.message) {
      url += `?text=${encodeURIComponent(newData?.message)}`;
    }
    onContentChange?.(url);
  };

  const parseEmailData = (mailto) => {
    if (!mailto?.includes('mailto:')) return {};
    try {
      const urlObj = new URL(mailto);
      const email = urlObj?.pathname || '';
      const subject = urlObj?.searchParams?.get('subject') || '';
      const body = urlObj?.searchParams?.get('body') || '';
      return { email, subject, body };
    } catch {
      return {};
    }
  };

  const updateEmailField = (field, value) => {
    const currentData = parseEmailData(content);
    const newData = { ...currentData, [field]: value };
    let mailto = `mailto:${newData?.email || ''}`;
    const params = new URLSearchParams();
    if (newData?.subject) params?.append('subject', newData?.subject);
    if (newData?.body) params?.append('body', newData?.body);
    const queryString = params?.toString();
    if (queryString) mailto += `?${queryString}`;
    onContentChange?.(mailto);
  };

  const parseWiFiData = (wifiString) => {
    if (!wifiString?.includes('WIFI:')) return {};
    try {
      const parts = wifiString?.match(/WIFI:T:([^;]*);S:([^;]*);P:([^;]*);H:([^;]*)/);
      if (parts) {
        return {
          security: parts?.[1],
          ssid: parts?.[2],
          password: parts?.[3],
          hidden: parts?.[4] === 'true'
        };
      }
      return {};
    } catch {
      return {};
    }
  };

  const updateWiFiField = (field, value) => {
    const currentData = parseWiFiData(content);
    const newData = { ...currentData, [field]: value };
    const wifi = `WIFI:T:${newData?.security || 'WPA'};S:${newData?.ssid || ''};P:${newData?.password || ''};H:false;;`;
    onContentChange?.(wifi);
  };

  const parseSMSData = (sms) => {
    if (!sms?.includes('sms:')) return {};
    try {
      const urlObj = new URL(sms);
      const phone = urlObj?.pathname || '';
      const message = urlObj?.searchParams?.get('body') || '';
      return { phone, message };
    } catch {
      return {};
    }
  };

  const updateSMSField = (field, value) => {
    const currentData = parseSMSData(content);
    const newData = { ...currentData, [field]: value };
    let sms = `sms:${newData?.phone || ''}`;
    if (newData?.message) {
      sms += `?body=${encodeURIComponent(newData?.message)}`;
    }
    onContentChange?.(sms);
  };

  return (
    <div>
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Content Configuration</h3>
      {/* QR Code Name */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          QR Code Name *
        </label>
        <input
          type="text"
          value={name}
          onChange={(e) => onNameChange?.(e?.target?.value)}
          placeholder="Enter a name for this QR code"
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <p className="text-xs text-gray-500 mt-1">
          Give your QR code a memorable name for easy identification
        </p>
      </div>

      {/* NEW: Custom Short Code Section with improved UX */}
      <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="useCustomShortCode"
              checked={localUseCustomShortCode}
              onChange={(e) => handleCustomShortCodeToggle(e?.target?.checked)}
              className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <label htmlFor="useCustomShortCode" className="text-sm font-medium text-gray-700">
              Customize Short URL
            </label>
          </div>
          
          {/* Enhanced suggestions button - only show when checkbox is checked */}
          <div className="flex items-center space-x-2">
            {localUseCustomShortCode && name?.trim() && (
              <button
                type="button"
                onClick={generateSuggestionsFromName}
                disabled={isGeneratingSuggestions}
                className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-100 border border-blue-300 rounded-md hover:bg-blue-200 hover:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
              >
                {isGeneratingSuggestions ? (
                  <>
                    <RefreshCw className="w-3 h-3 mr-1 animate-spin" />
                    Getting suggestions...
                  </>
                ) : (
                  <>
                    <Icon name="Lightbulb" size={12} className="mr-1" />
                    Get suggestions from name
                  </>
                )}
              </button>
            )}
          </div>
        </div>

        {localUseCustomShortCode && (
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Custom Short Code
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={localCustomShortCode}
                  onChange={(e) => {
                    const value = e?.target?.value?.toLowerCase()?.replace(/[^a-z0-9_-]/g, '');
                    handleCustomShortCodeChange(value);
                  }}
                  placeholder="my-custom-link"
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                    shortCodeValidation?.isValid === false 
                      ? 'border-red-300 focus:ring-red-500' 
                      : shortCodeValidation?.isValid === true
                      ? 'border-green-300 focus:ring-green-500' :'border-gray-300 focus:ring-blue-500'
                  }`}
                  maxLength="50"
                />
                {isValidatingShortCode && (
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                  </div>
                )}
              </div>
              
              {/* URL Preview */}
              {localCustomShortCode && (
                <p className="text-xs text-gray-600 mt-1">
                  Your URL:{' '}
                  <span className="font-mono bg-gray-100 px-1 py-0.5 rounded">
                    {(import.meta.env?.VITE_TRACKING_BASE_URL || 'https://qr.taylortravelmanagement.com')?.replace(/^https?:\/\//, '')}/{localCustomShortCode}
                  </span>
                </p>
              )}

              {/* Validation Messages */}
              {shortCodeValidation && (
                <div className="mt-2">
                  {shortCodeValidation?.isValid ? (
                    <p className="text-xs text-green-600 flex items-center">
                      <Icon name="CheckCircle" size={12} className="mr-1" />
                      Short code is available!
                    </p>
                  ) : (
                    <p className="text-xs text-red-600 flex items-center">
                      <Icon name="XCircle" size={12} className="mr-1" />
                      {shortCodeValidation?.error}
                    </p>
                  )}
                </div>
              )}

              {/* Enhanced suggestions button inside custom short code section - only when checkbox is checked */}
              {!showSuggestions && name?.trim() && (
                <div className="mt-3 flex items-center justify-between">
                  <p className="text-xs text-gray-600">Need ideas for your short code?</p>
                  <button
                    type="button"
                    onClick={generateSuggestionsFromName}
                    disabled={isGeneratingSuggestions}
                    className="inline-flex items-center px-2 py-1 text-xs font-medium text-blue-700 bg-blue-100 border border-blue-300 rounded hover:bg-blue-200 hover:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                  >
                    {isGeneratingSuggestions ? (
                      <>
                        <RefreshCw className="w-3 h-3 mr-1 animate-spin" />
                        Loading...
                      </>
                    ) : (
                      <>
                        <Icon name="Lightbulb" size={12} className="mr-1" />
                        Generate ideas
                      </>
                    )}
                  </button>
                </div>
              )}
            </div>

            {/* Enhanced Suggestions Display - only shown when checkbox is checked */}
            {showSuggestions && shortCodeSuggestions?.length > 0 && (
              <div className="bg-white border border-gray-200 rounded-md p-3">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-sm font-medium text-gray-800 flex items-center">
                    <Icon name="Lightbulb" size={14} className="mr-1 text-yellow-500" />
                    Suggested short codes based on "{name}"
                  </p>
                  <button
                    type="button"
                    onClick={() => setShowSuggestions(false)}
                    className="text-gray-400 hover:text-gray-600 focus:outline-none"
                  >
                    <Icon name="X" size={14} />
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {shortCodeSuggestions?.slice(0, 6)?.map((suggestion, index) => (
                    <button
                      key={index}
                      type="button"
                      onClick={() => applySuggestion(suggestion)}
                      className="group px-3 py-2 text-sm bg-gray-50 border border-gray-300 rounded-md hover:bg-blue-50 hover:border-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-200 text-left"
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-mono text-gray-700 group-hover:text-blue-700">{suggestion}</span>
                        <Icon name="ArrowRight" size={12} className="text-gray-400 group-hover:text-blue-500 opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
                      </div>
                    </button>
                  ))}
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  Click any suggestion to use it, or modify them as needed
                </p>
              </div>
            )}

            {/* Show message when suggestions are empty - only when checkbox is checked */}
            {showSuggestions && shortCodeSuggestions?.length === 0 && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3">
                <p className="text-sm text-yellow-800 flex items-center">
                  <Icon name="AlertTriangle" size={14} className="mr-2" />
                  No available suggestions found for "{name}". Try a different name or create your own short code.
                </p>
              </div>
            )}
          </div>
        )}

        {!localUseCustomShortCode && (
          <div className="space-y-2">
            <p className="text-xs text-gray-600">
              Short code will be randomly generated (e.g., "ab3f7d2k"). Enable custom short code above to use your own text.
            </p>
            {/* Removed suggestion button when custom short code is disabled */}
          </div>
        )}
      </div>

      {/* Dynamic content fields based on QR type */}
      <div className="mb-6">
        {getFormFields()}
      </div>
      {/* Campaign Selection - NEW PROMINENT LOCATION */}
      <div className="mb-6 p-4 bg-gray-50 border border-gray-200 rounded-lg">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="useCampaign"
              checked={useCampaign}
              onChange={(e) => handleCampaignToggle(e?.target?.checked)}
              className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <label htmlFor="useCampaign" className="text-sm font-medium text-gray-700">
              Assign to Campaign
            </label>
          </div>
          
          {useCampaign && (
            <button
              onClick={onRefreshCampaigns}
              disabled={loadingCampaigns}
              className="p-1 text-gray-500 hover:text-gray-700 disabled:text-gray-400"
              title="Refresh Campaigns"
            >
              <RefreshCw className={`w-4 h-4 ${loadingCampaigns ? 'animate-spin' : ''}`} />
            </button>
          )}
        </div>

        {useCampaign && (
          <div className="space-y-3">
            {/* Campaign Dropdown */}
            <div className="relative">
              <button
                type="button"
                onClick={() => setShowCampaignDropdown(!showCampaignDropdown)}
                className="w-full px-3 py-2 text-left bg-white border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 flex items-center justify-between"
                disabled={loadingCampaigns}
              >
                <span className="text-sm">
                  {selectedCampaign ? selectedCampaign?.name : 'Select a campaign...'}
                </span>
                <ChevronDown className={`w-4 h-4 text-gray-500 transform transition-transform ${showCampaignDropdown ? 'rotate-180' : ''}`} />
              </button>

              {/* Dropdown Menu */}
              {showCampaignDropdown && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-48 overflow-y-auto">
                  {loadingCampaigns ? (
                    <div className="p-3 text-center text-gray-500">
                      <RefreshCw className="w-4 h-4 animate-spin mx-auto mb-1" />
                      <span className="text-xs">Loading campaigns...</span>
                    </div>
                  ) : campaigns?.length === 0 ? (
                    <div className="p-3 text-center text-gray-500">
                      <Tag className="w-6 h-6 text-gray-300 mx-auto mb-2" />
                      <p className="text-xs font-medium mb-1">No campaigns available</p>
                      <p className="text-xs">Create your first campaign to track QR performance</p>
                    </div>
                  ) : (
                    <>
                      {/* No Campaign Option */}
                      <button
                        type="button"
                        onClick={() => handleCampaignSelect(null)}
                        className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 border-b border-gray-100"
                      >
                        <span className="font-medium text-gray-500">No Campaign</span>
                        <p className="text-xs text-gray-400">Don't track with any campaign</p>
                      </button>
                      
                      {/* Campaign Options */}
                      {campaigns?.map((campaign) => (
                        <button
                          key={campaign?.id}
                          type="button"
                          onClick={() => handleCampaignSelect(campaign)}
                          className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 border-b border-gray-100 last:border-b-0"
                        >
                          <div className="flex items-center justify-between mb-1">
                            <span className="font-medium text-gray-900">{campaign?.name}</span>
                            <span className={`px-2 py-0.5 text-xs rounded ${
                              campaign?.status === 'active' ? 'bg-green-100 text-green-800' :
                              campaign?.status === 'draft' ? 'bg-gray-100 text-gray-800' :
                              campaign?.status === 'paused'? 'bg-yellow-100 text-yellow-800' : 'bg-blue-100 text-blue-800'
                            }`}>
                              {campaign?.status}
                            </span>
                          </div>
                          {campaign?.description && (
                            <p className="text-xs text-gray-500 truncate">{campaign?.description}</p>
                          )}
                        </button>
                      ))}
                    </>
                  )}
                </div>
              )}
            </div>

            {/* Selected Campaign Info */}
            {selectedCampaign && (
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-md">
                <div className="flex items-center space-x-2 mb-1">
                  <Tag className="w-4 h-4 text-blue-600" />
                  <span className="text-sm font-medium text-blue-800">Campaign Selected</span>
                </div>
                <p className="text-xs text-blue-700">
                  This QR code will be tracked under "<strong>{selectedCampaign?.name}</strong>". 
                  UTM parameters will be automatically added for web URLs.
                </p>
              </div>
            )}
          </div>
        )}

        {!useCampaign && (
          <p className="text-xs text-gray-600 mt-2">
            Enable campaign tracking to organize your QR codes and analyze performance metrics
          </p>
        )}
      </div>
      {/* Preview text */}
      {content && (
        <div className="mt-6 p-4 bg-gray-50 rounded-lg">
          <div className="flex items-start space-x-2">
            <HelpCircle className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-gray-700 mb-1">Preview Data:</p>
              <p className="text-xs text-gray-600 break-all font-mono bg-white p-2 rounded border">
                {content?.slice(0, 200)}{content?.length > 200 ? '...' : ''}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QRContentForm;